/**
 * useMinistryWorkspace — fetches the Ministry workspace row by slug and
 * exposes its branding/feature settings to the shell.
 *
 * Returns null while loading or if the user isn't a member. The root
 * /mohap guard (MinistryShell) redirects unauthorized users; this hook
 * just hands the shell the data it needs to render branding, theme
 * tokens, and the feature flag map.
 *
 * The query is workspace-scoped via RLS — a non-member won't see any
 * row come back (workspaces table has its own RLS policy gating reads
 * by workspace_members membership). That's the right behavior: if the
 * user isn't in the Ministry workspace, they shouldn't be on the
 * /mohap path prefix in the first place.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMinistryEdition } from "./useMinistryEdition";

export interface MinistryBranding {
  name: string;
  short_name: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
}

export interface MinistryFeatures {
  chat_enabled: boolean;
  brief_surface: boolean;
  options_paper: boolean;
  decision_log: boolean;
  risk_register: boolean;
  transformation_dashboard: boolean;
  institutional_memory: boolean;
  review_queue: boolean;
}

export interface MinistryWorkspace {
  id: string;
  slug: string;
  name: string;
  edition: string;
  branding: MinistryBranding;
  features: MinistryFeatures;
  language: string;
  brief_schema: string;
}

export function useMinistryWorkspace() {
  const { workspaceSlug, isWhiteLabel } = useMinistryEdition();
  const [workspace, setWorkspace] = useState<MinistryWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isWhiteLabel) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: queryError } = await supabase
        .from("workspaces")
        .select("id, slug, name, settings")
        .eq("slug", workspaceSlug)
        .maybeSingle();

      if (cancelled) return;

      if (queryError) {
        setError(queryError.message);
        setLoading(false);
        return;
      }
      if (!data) {
        setError("Ministry workspace not visible — check membership");
        setLoading(false);
        return;
      }

      const settings = (data.settings ?? {}) as Record<string, unknown>;
      setWorkspace({
        id: data.id,
        slug: data.slug,
        name: data.name,
        edition: (settings.edition as string) ?? "ministry-moh",
        branding: (settings.branding as MinistryBranding) ?? {
          name: data.name,
          short_name: data.name.slice(0, 4).toUpperCase(),
          logo_url: null,
          primary_color: "#1a5632",
          accent_color: "#c5a44e",
        },
        features: (settings.features as MinistryFeatures) ?? {
          chat_enabled: false,
          brief_surface: true,
          options_paper: true,
          decision_log: true,
          risk_register: true,
          transformation_dashboard: true,
          institutional_memory: true,
          review_queue: true,
        },
        language: (settings.language as string) ?? "en",
        brief_schema: (settings.brief_schema as string) ?? "ministerial_9part",
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isWhiteLabel, workspaceSlug]);

  return { workspace, loading, error };
}
